/**
 * Question Utilities
 * 
 * Pure utility functions for question manipulation, filtering, and processing.
 * These functions are stateless and can be used anywhere in the application.
 * 
 * @module questionUtils
 */

/**
 * Fisher-Yates shuffle algorithm for randomizing array order
 * 
 * @param {Array} array - Array to shuffle
 * @returns {Array} New shuffled array (original array is not modified)
 * 
 * @example
 * const shuffled = shuffleOptions(['A', 'B', 'C', 'D']);
 * // Returns: ['C', 'A', 'D', 'B'] (random order)
 */
export const shuffleOptions = (array) => {
  if (!Array.isArray(array) || array.length === 0) {
    return [];
  }

  // Create a copy to avoid mutating the original array
  const shuffled = [...array];
  
  // Fisher-Yates shuffle algorithm
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
};

/**
 * Filter spelling words by month
 * 
 * @param {Array} spellingWords - Array of spelling words
 * @param {string} month - Month identifier ('october', 'november', 'december', 'january', 'february', 'march', 'additional', or 'all')
 * @returns {Array} Filtered array of spelling words for the specified month
 * 
 * @example
 * const octoberWords = filterSpellingWordsByMonth(allWords, 'october');
 * // Returns: words[0..10]
 */
export const filterSpellingWordsByMonth = (spellingWords, month) => {
  if (!Array.isArray(spellingWords)) {
    return [];
  }

  if (month === 'all') {
    return spellingWords;
  }
  
  const monthRanges = {
    'october': { start: 0, end: 10 },
    'november': { start: 10, end: 20 },
    'december': { start: 20, end: 30 },
    'january': { start: 30, end: 40 },
    'february': { start: 40, end: 50 },
    'march': { start: 50, end: 60 },
    'additional': { start: 60, end: spellingWords.length }
  };
  
  const range = monthRanges[month];
  if (!range) {
    return spellingWords;
  }
  
  return spellingWords.slice(range.start, range.end);
};

/**
 * Filter phonics questions by difficulty level
 * 
 * @param {Object} phonicsQuestions - Object with difficulty keys: { easy: [], medium: [], hard: [] }
 * @param {string} difficulty - Difficulty level ('easy', 'medium', 'hard')
 * @returns {Array} Questions for the specified difficulty level
 * 
 * @example
 * const easyQuestions = filterPhonicsQuestionsByDifficulty(phonicsData, 'easy');
 */
export const filterPhonicsQuestionsByDifficulty = (phonicsQuestions, difficulty) => {
  if (!phonicsQuestions || typeof phonicsQuestions !== 'object') {
    return [];
  }

  return phonicsQuestions[difficulty] || phonicsQuestions.medium || [];
};

/**
 * Filter math questions by difficulty level
 * 
 * @param {Object} mathQuestions - Object with difficulty keys: { easy: [], medium: [], hard: [] }
 * @param {string} difficulty - Difficulty level ('easy', 'medium', 'hard')
 * @returns {Array} Questions for the specified difficulty level
 * 
 * @example
 * const hardQuestions = filterMathQuestionsByDifficulty(mathData, 'hard');
 */
export const filterMathQuestionsByDifficulty = (mathQuestions, difficulty) => {
  if (!mathQuestions || typeof mathQuestions !== 'object') {
    return [];
  }

  return mathQuestions[difficulty] || mathQuestions.medium || [];
};

/**
 * Filter reading questions by category
 * 
 * @param {Array} readingQuestions - Array of reading questions
 * @param {string} category - Category filter ('all' returns all questions)
 * @returns {Array} Filtered questions (currently returns all for any category)
 * 
 * @example
 * const allQuestions = filterReadingQuestionsByCategory(readingData, 'all');
 */
export const filterReadingQuestionsByCategory = (readingQuestions, category) => {
  if (!Array.isArray(readingQuestions)) {
    return [];
  }

  if (category === 'all') {
    return readingQuestions;
  }
  
  // For now, return all reading questions since they're not categorized
  // In the future, we could add categories like 'story-elements', 'comprehension', etc.
  return readingQuestions;
};

/**
 * Calculate total question count for a subject
 * 
 * @param {string} subject - Subject identifier
 * @param {Object} questionData - Object containing question arrays for different subjects
 * @param {Object} questionData.enhancedPhonicQuestions - Phonics questions by difficulty
 * @param {Object} questionData.enhancedMathQuestions - Math questions by difficulty
 * @param {Array} questionData.enhancedReadingQuestions - Reading questions
 * @param {Array} questionData.enhancedSpellingWords - Spelling words
 * @param {Array} questionData.scienceQuestions - Science questions
 * @param {Array} questionData.artQuestions - Art questions
 * @param {Array} questionData.geographyQuestions - Geography questions
 * @param {Array} questionData.historyQuestions - History questions
 * @param {Array} [questionData.socialStudiesQuestions] - Social Studies questions
 * @param {Array} [questionData.skipCountingQuestions] - Skip Counting questions
 * @returns {number} Total question count for the subject
 * 
 * @example
 * const count = getQuestionCount('phonics', {
 *   enhancedPhonicQuestions: { easy: [10], medium: [15], hard: [5] }
 * });
 * // Returns: 30
 */
export const getQuestionCount = (subject, questionData) => {
  const {
    enhancedPhonicQuestions,
    enhancedMathQuestions,
    enhancedReadingQuestions,
    enhancedSpellingWords,
    scienceQuestions,
    artQuestions,
    geographyQuestions,
    historyQuestions,
    socialStudiesQuestions,
    skipCountingQuestions,
  } = questionData;

  switch (subject) {
    case 'phonics':
      return (enhancedPhonicQuestions?.easy?.length || 0) + 
             (enhancedPhonicQuestions?.medium?.length || 0) + 
             (enhancedPhonicQuestions?.hard?.length || 0);
    
    case 'math':
      return (enhancedMathQuestions?.easy?.length || 0) + 
             (enhancedMathQuestions?.medium?.length || 0) + 
             (enhancedMathQuestions?.hard?.length || 0);
    
    case 'reading':
      return Array.isArray(enhancedReadingQuestions) ? enhancedReadingQuestions.length : 0;
    
    case 'spelling':
      return Array.isArray(enhancedSpellingWords) ? enhancedSpellingWords.length : 0;
    
    case 'science':
      return Array.isArray(scienceQuestions) ? scienceQuestions.length : 0;
    
    case 'art':
      return Array.isArray(artQuestions) ? artQuestions.length : 0;
    
    case 'geography':
      return Array.isArray(geographyQuestions) ? geographyQuestions.length : 0;
    
    case 'history':
      return Array.isArray(historyQuestions) ? historyQuestions.length : 0;
    
    case 'social':
      return Array.isArray(socialStudiesQuestions) ? socialStudiesQuestions.length : 0;
    
    case 'skipcounting':
      return Array.isArray(skipCountingQuestions) ? skipCountingQuestions.length : 0;
    
    default:
      return 0;
  }
};

/**
 * Get current month identifier for spelling word selection
 * 
 * @returns {string} Month identifier ('october', 'november', 'december', 'january', 'february', 'march', or 'all')
 * 
 * @example
 * const currentMonth = getCurrentMonth();
 * // Returns: 'november' (if current month is November)
 */
export const getCurrentMonth = () => {
  const now = new Date();
  const month = now.getMonth(); // 0-11 (January = 0)
  const monthMap = {
    9: 'october',   // October
    10: 'november', // November  
    11: 'december', // December
    0: 'january',   // January
    1: 'february',  // February
    2: 'march'      // March
  };
  return monthMap[month] || 'all'; // Default to 'all' if not in our range
};

/**
 * Answer Validation Utilities
 * 
 * Pure functions for answer comparison, validation, and scoring.
 */

/**
 * Normalize an answer string for comparison
 * - Trims whitespace
 * - Converts to lowercase
 * - Handles null/undefined values
 * 
 * @param {string} answer - Answer string to normalize
 * @returns {string} Normalized answer string
 * 
 * @example
 * normalizeAnswer('  Hello World  ') // Returns: 'hello world'
 * normalizeAnswer(null) // Returns: ''
 */
export const normalizeAnswer = (answer) => {
  if (answer == null) {
    return '';
  }
  return String(answer).trim().toLowerCase();
};

/**
 * Compare two answers for equality
 * Performs case-insensitive, whitespace-trimmed comparison
 * 
 * @param {string} selectedAnswer - The selected answer
 * @param {string} correctAnswer - The correct answer
 * @returns {boolean} True if answers match (after normalization)
 * 
 * @example
 * compareAnswers('Hello', 'hello') // Returns: true
 * compareAnswers('  Test  ', 'test') // Returns: true
 * compareAnswers('A', 'B') // Returns: false
 */
export const compareAnswers = (selectedAnswer, correctAnswer) => {
  const normalizedSelected = normalizeAnswer(selectedAnswer);
  const normalizedCorrect = normalizeAnswer(correctAnswer);
  return normalizedSelected === normalizedCorrect;
};

/**
 * Get the correct answer from a question object
 * Checks both 'correct' and 'answer' properties
 * Supports both single answers (string) and multiple-select (array)
 * 
 * @param {Object} question - Question object
 * @param {string|Array} [question.correct] - Correct answer property (string or array)
 * @param {string|Array} [question.answer] - Alternative answer property (string or array)
 * @returns {string|Array} The correct answer(s), or empty string/array if not found
 * 
 * @example
 * getCorrectAnswer({ correct: 'Paris' }) // Returns: 'Paris'
 * getCorrectAnswer({ correct: ['Chocolate', 'Butter'] }) // Returns: ['Chocolate', 'Butter']
 * getCorrectAnswer({ answer: 'London' }) // Returns: 'London'
 * getCorrectAnswer({}) // Returns: ''
 */
export const getCorrectAnswer = (question) => {
  if (!question || typeof question !== 'object') {
    return '';
  }
  return question.correct || question.answer || '';
};

/**
 * Check if a question is a multiple-select type
 * 
 * @param {Object} question - Question object
 * @returns {boolean} True if question type is 'multiple-select'
 * 
 * @example
 * isMultipleSelect({ type: 'multiple-select' }) // Returns: true
 * isMultipleSelect({ type: 'multiple-choice' }) // Returns: false
 */
export const isMultipleSelect = (question) => {
  return question?.type === 'multiple-select';
};

/**
 * Validate if an answer is correct
 * Supports both single-select and multiple-select questions
 * 
 * @param {string|Array} selectedAnswer - The selected answer(s)
 * @param {Object} question - Question object with correct/answer property
 * @returns {boolean} True if the selected answer matches the correct answer
 * 
 * @example
 * validateAnswer('Paris', { correct: 'Paris' }) // Returns: true
 * validateAnswer(['Chocolate', 'Butter'], { correct: ['Chocolate', 'Butter'], type: 'multiple-select' }) // Returns: true
 * validateAnswer('paris', { correct: 'Paris' }) // Returns: true (case-insensitive)
 */
export const validateAnswer = (selectedAnswer, question) => {
  const correctAnswer = getCorrectAnswer(question);
  
  // Handle multiple-select questions
  if (isMultipleSelect(question)) {
    if (!Array.isArray(correctAnswer) || !Array.isArray(selectedAnswer)) {
      return false;
    }
    
    // Normalize both arrays for comparison
    const normalizedCorrect = correctAnswer.map(a => normalizeAnswer(a)).sort();
    const normalizedSelected = selectedAnswer.map(a => normalizeAnswer(a)).sort();
    
    // Check if arrays have same length and all elements match
    if (normalizedCorrect.length !== normalizedSelected.length) {
      return false;
    }
    
    return normalizedCorrect.every((ans, idx) => ans === normalizedSelected[idx]);
  }
  
  // Handle single-select questions
  return compareAnswers(selectedAnswer, correctAnswer);
};

/**
 * Calculate points earned for an answer
 * 
 * @param {boolean} isCorrect - Whether the answer is correct
 * @param {boolean} hintUsed - Whether a hint was used (reduces points by half)
 * @param {number} [pointsPerCorrect=10] - Points awarded for correct answer
 * @returns {number} Points earned (0 if incorrect, full points if correct, half if hint used)
 * 
 * @example
 * calculatePoints(true, false) // Returns: 10
 * calculatePoints(true, true) // Returns: 5 (hint used)
 * calculatePoints(false, false) // Returns: 0
 */
export const calculatePoints = (isCorrect, hintUsed = false, pointsPerCorrect = 10) => {
  if (!isCorrect) {
    return 0;
  }
  
  const basePoints = pointsPerCorrect;
  return hintUsed ? Math.floor(basePoints / 2) : basePoints;
};

/**
 * Format answer for display
 * Trims whitespace and handles empty/null values
 * 
 * @param {string} answer - Answer to format
 * @param {string} [defaultValue=''] - Default value if answer is empty/null
 * @returns {string} Formatted answer string
 * 
 * @example
 * formatAnswerForDisplay('  Hello  ') // Returns: 'Hello'
 * formatAnswerForDisplay(null) // Returns: ''
 */
export const formatAnswerForDisplay = (answer, defaultValue = '') => {
  if (answer == null) {
    return defaultValue;
  }
  return String(answer).trim();
};

/**
 * Check if a question is a true/false question
 * True/false questions should NOT be expanded to 4 options
 * 
 * @param {Object} question - Question object
 * @returns {boolean} True if this is a true/false question
 */
const isTrueFalseQuestion = (question) => {
  if (!question || !Array.isArray(question.options) || question.options.length !== 2) {
    return false;
  }
  
  const options = question.options.map(opt => String(opt).toLowerCase().trim());
  
  // Check for common true/false patterns
  const trueFalsePatterns = [
    ['true', 'false'],
    ['yes', 'no'],
    ['correct', 'incorrect'],
    ['right', 'wrong'],
    ['t', 'f']
  ];
  
  return trueFalsePatterns.some(pattern => {
    const [opt1, opt2] = pattern;
    return (options.includes(opt1) && options.includes(opt2));
  });
};

/**
 * Generate plausible distractors for a question
 * Creates 2 additional options based on the question type and existing options
 * 
 * @param {Object} question - Question object
 * @param {Array} existingOptions - Current options (should be 2)
 * @param {string} correctAnswer - The correct answer
 * @returns {Array} Array of 2 distractor options
 */
const generateDistractors = (question, existingOptions, correctAnswer) => {
  const distractors = [];
  const questionText = String(question.question || question.word || '').toLowerCase();
  const correct = String(correctAnswer).trim();
  const wrongOption = existingOptions.find(opt => String(opt).trim() !== correct) || existingOptions[0];
  const allOptions = [...existingOptions, correct];
  const usedOptions = new Set(allOptions.map(o => String(o).trim().toLowerCase()));
  
  // Helper to check if option is already used
  const isUsed = (opt) => usedOptions.has(String(opt).trim().toLowerCase());
  
  // Helper to add distractor if not used
  const addDistractor = (opt) => {
    if (!isUsed(opt) && distractors.length < 2) {
      distractors.push(String(opt));
      usedOptions.add(String(opt).trim().toLowerCase());
    }
  };
  
  // Try to parse numbers for math questions first (takes priority)
  const correctNum = parseFloat(correct);
  const wrongNum = parseFloat(wrongOption);
  
  if (!isNaN(correctNum) && !isNaN(wrongNum)) {
    // Math question - generate number-based distractors
    const diff = Math.abs(correctNum - wrongNum);
    const smaller = Math.min(correctNum, wrongNum);
    const larger = Math.max(correctNum, wrongNum);
    
    // Generate distractors that are close but wrong
    if (diff === 1) {
      // Options differ by 1, add numbers nearby
      addDistractor(String(Math.max(0, smaller - 1))); // Don't go below 0
      addDistractor(String(larger + 1));
    } else if (diff <= 5) {
      // Options are close together, add numbers nearby
      addDistractor(String(Math.max(0, smaller - 1)));
      addDistractor(String(larger + 1));
    } else {
      // Options differ by more, add numbers in between and outside
      const mid = Math.round((correctNum + wrongNum) / 2);
      // Make sure mid is different from both options
      if (mid !== correctNum && mid !== wrongNum) {
        addDistractor(String(mid));
      } else {
        addDistractor(String(Math.max(0, smaller - 2)));
      }
      addDistractor(String(larger + Math.max(1, Math.floor(diff / 2))));
    }
  } else if (questionText.includes('bigger') || questionText.includes('greater') || questionText.includes('more')) {
    // Comparison question - add other numbers
    if (!isNaN(correctNum)) {
      addDistractor(String(correctNum + 1));
      addDistractor(String(Math.max(0, correctNum - 1)));
    } else {
      // For non-numeric comparison, try to find related terms
      generateSemanticDistractors(questionText, allOptions, addDistractor);
    }
  } else {
    // Check for proper nouns (capitalized names, places, etc.) for non-math questions
    const hasProperNoun = (str) => {
      const words = str.split(/\s+/);
      return words.some(word => word.length > 0 && word[0] === word[0].toUpperCase() && /[A-Z]/.test(word[0]));
    };
    
    // Handle proper nouns (names, places, specific terms)
    if (hasProperNoun(correct) || hasProperNoun(wrongOption)) {
      // Extract the category from question text
      if (questionText.includes('president') || questionText.includes('washington') || questionText.includes('lincoln') || questionText.includes('jefferson')) {
        const presidents = ['John Adams', 'James Madison', 'Andrew Jackson', 'Theodore Roosevelt'];
        presidents.forEach(p => addDistractor(p));
      } else if (questionText.includes('invent') || questionText.includes('edison') || questionText.includes('bell')) {
        const inventors = ['Nikola Tesla', 'Benjamin Franklin', 'Samuel Morse', 'Guglielmo Marconi'];
        inventors.forEach(i => addDistractor(i));
      } else if (questionText.includes('planet') || questionText.includes('mars') || questionText.includes('venus')) {
        const planets = ['Jupiter', 'Saturn', 'Neptune', 'Uranus'];
        planets.forEach(p => addDistractor(p));
      } else if (questionText.includes('ship') || questionText.includes('mayflower')) {
        const ships = ['Santa Maria', 'Nina', 'Pinta', 'Titanic'];
        ships.forEach(s => addDistractor(s));
      } else {
        // Generic proper noun handling - try semantic distractors
        generateSemanticDistractors(questionText, allOptions, addDistractor);
      }
    } else if (existingOptions[0].length <= 3 && existingOptions[1].length <= 3) {
      // Short options (likely phonics or simple choices)
      const commonPhonics = ['TH', 'SH', 'CH', 'PH', 'CK', 'WH'];
      const commonMath = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
      
      // Check if it's phonics
      if (commonPhonics.some(ph => allOptions.some(opt => String(opt).includes(ph)))) {
        commonPhonics.forEach(ph => addDistractor(ph));
      } else if (commonMath.some(m => allOptions.includes(m))) {
        // Math-like options
        commonMath.forEach(m => addDistractor(m));
      } else {
        // Try semantic distractors for short options too
        generateSemanticDistractors(questionText, allOptions, addDistractor);
      }
    } else {
      // Longer text options - generate semantically related alternatives
      generateSemanticDistractors(questionText, allOptions, addDistractor);
    }
  }
  
  // Ensure we have exactly 2 distractors
  while (distractors.length < 2) {
    addDistractor(`Option ${String.fromCharCode(65 + distractors.length)}`);
  }
  
  return distractors.slice(0, 2);
};

/**
 * Generate semantically related distractors based on question topic
 * @param {string} questionText - The question text in lowercase
 * @param {Array} existingOptions - Already used options
 * @param {Function} addDistractor - Function to add a distractor if not used
 */
const generateSemanticDistractors = (questionText, existingOptions, addDistractor) => {
  // Water cycle and weather terms
  if (questionText.includes('rain') || questionText.includes('snow') || questionText.includes('hail') || 
      questionText.includes('precipitation') || questionText.includes('water cycle') ||
      questionText.includes('evaporation') || questionText.includes('condensation')) {
    const waterCycleTerms = ['Condensation', 'Transpiration', 'Collection', 'Runoff', 'Infiltration'];
    waterCycleTerms.forEach(term => addDistractor(term));
  }
  
  // Weather terms
  if (questionText.includes('weather') || questionText.includes('cloud') || questionText.includes('storm') ||
      questionText.includes('wind') || questionText.includes('temperature') || questionText.includes('season')) {
    const weatherTerms = ['Humidity', 'Pressure', 'Forecast', 'Climate', 'Atmosphere'];
    weatherTerms.forEach(term => addDistractor(term));
  }
  
  // Plant terms
  if (questionText.includes('plant') || questionText.includes('tree') || questionText.includes('flower') ||
      questionText.includes('leaf') || questionText.includes('root') || questionText.includes('seed')) {
    const plantTerms = ['Stem', 'Petal', 'Pollen', 'Photosynthesis', 'Chlorophyll'];
    plantTerms.forEach(term => addDistractor(term));
  }
  
  // Animal terms
  if (questionText.includes('animal') || questionText.includes('habitat') || questionText.includes('species') ||
      questionText.includes('mammal') || questionText.includes('bird') || questionText.includes('fish')) {
    const animalTerms = ['Ecosystem', 'Predator', 'Prey', 'Migration', 'Adaptation'];
    animalTerms.forEach(term => addDistractor(term));
  }
  
  // Space and astronomy
  if (questionText.includes('planet') || questionText.includes('star') || questionText.includes('sun') ||
      questionText.includes('moon') || questionText.includes('solar') || questionText.includes('space') ||
      questionText.includes('earth') || questionText.includes('orbit')) {
    const spaceTerms = ['Galaxy', 'Asteroid', 'Comet', 'Nebula', 'Constellation'];
    spaceTerms.forEach(term => addDistractor(term));
  }
  
  // Human body
  if (questionText.includes('body') || questionText.includes('organ') || questionText.includes('bone') ||
      questionText.includes('muscle') || questionText.includes('heart') || questionText.includes('brain') ||
      questionText.includes('lung') || questionText.includes('sense')) {
    const bodyTerms = ['Tissue', 'Cell', 'Nerve', 'Vein', 'Artery'];
    bodyTerms.forEach(term => addDistractor(term));
  }
  
  // Matter and chemistry
  if (questionText.includes('matter') || questionText.includes('solid') || questionText.includes('liquid') ||
      questionText.includes('gas') || questionText.includes('melt') || questionText.includes('freeze') ||
      questionText.includes('dissolve') || questionText.includes('solution')) {
    const matterTerms = ['Mixture', 'Compound', 'Element', 'Molecule', 'Atom'];
    matterTerms.forEach(term => addDistractor(term));
  }
  
  // Force and motion
  if (questionText.includes('force') || questionText.includes('gravity') || questionText.includes('motion') ||
      questionText.includes('friction') || questionText.includes('magnetic') || questionText.includes('push') ||
      questionText.includes('pull')) {
    const forceTerms = ['Momentum', 'Velocity', 'Acceleration', 'Inertia', 'Energy'];
    forceTerms.forEach(term => addDistractor(term));
  }
  
  // Light and sound
  if (questionText.includes('light') || questionText.includes('sound') || questionText.includes('reflect') ||
      questionText.includes('vibration') || questionText.includes('wave')) {
    const lightSoundTerms = ['Refraction', 'Absorption', 'Frequency', 'Amplitude', 'Wavelength'];
    lightSoundTerms.forEach(term => addDistractor(term));
  }
  
  // Earth science
  if (questionText.includes('rock') || questionText.includes('mineral') || questionText.includes('ocean') ||
      questionText.includes('mountain') || questionText.includes('volcano') || questionText.includes('earthquake')) {
    const earthTerms = ['Sediment', 'Erosion', 'Fossil', 'Crust', 'Magma'];
    earthTerms.forEach(term => addDistractor(term));
  }
  
  // Geography terms
  if (questionText.includes('country') || questionText.includes('continent') || questionText.includes('ocean') ||
      questionText.includes('mountain') || questionText.includes('river') || questionText.includes('lake')) {
    const geographyTerms = ['Continent', 'Island', 'Peninsula', 'Valley', 'Desert'];
    geographyTerms.forEach(term => addDistractor(term));
  }
  
  // History and social studies
  if (questionText.includes('president') || questionText.includes('war') || questionText.includes('independence') ||
      questionText.includes('ancient') || questionText.includes('history') || questionText.includes('colony')) {
    const historyTerms = ['Revolution', 'Independence', 'Colony', 'Constitution', 'Declaration'];
    historyTerms.forEach(term => addDistractor(term));
  }
  
  // Chemistry and elements
  if (questionText.includes('element') || questionText.includes('atom') || questionText.includes('molecule') ||
      questionText.includes('chemical') || questionText.includes('reaction') || questionText.includes('compound')) {
    const chemistryTerms = ['Proton', 'Neutron', 'Electron', 'Ion', 'Bond'];
    chemistryTerms.forEach(term => addDistractor(term));
  }
  
  // Energy and electricity
  if (questionText.includes('electricity') || questionText.includes('energy') || questionText.includes('battery') ||
      questionText.includes('circuit') || questionText.includes('current') || questionText.includes('voltage')) {
    const energyTerms = ['Voltage', 'Current', 'Resistance', 'Power', 'Watt'];
    energyTerms.forEach(term => addDistractor(term));
  }
  
  // Ecology and environment
  if (questionText.includes('ecosystem') || questionText.includes('habitat') || questionText.includes('environment') ||
      questionText.includes('pollution') || questionText.includes('conservation') || questionText.includes('recycle')) {
    const ecologyTerms = ['Biodiversity', 'Conservation', 'Sustainability', 'Renewable', 'Ecosystem'];
    ecologyTerms.forEach(term => addDistractor(term));
  }
  
  // Astronomy and space
  if (questionText.includes('galaxy') || questionText.includes('asteroid') || questionText.includes('comet') ||
      questionText.includes('nebula') || questionText.includes('constellation') || questionText.includes('telescope')) {
    const astronomyTerms = ['Supernova', 'Black hole', 'Nebula', 'Quasar', 'Pulsar'];
    astronomyTerms.forEach(term => addDistractor(term));
  }
  
  // Anatomy and physiology
  if (questionText.includes('organ') || questionText.includes('system') || questionText.includes('tissue') ||
      questionText.includes('cell') || questionText.includes('muscle') || questionText.includes('bone')) {
    const anatomyTerms = ['Organelle', 'Membrane', 'Nucleus', 'Cytoplasm', 'Mitochondria'];
    anatomyTerms.forEach(term => addDistractor(term));
  }
  
  // If we still don't have enough distractors, try to use related words from the question
  // Extract key nouns from question
  const keyWords = questionText.split(/\s+/).filter(word => 
    word.length > 4 && !['what', 'when', 'where', 'which', 'how', 'does', 'do', 'are', 'is', 'the', 'a', 'an', 'call', 'happens', 'happened'].includes(word)
  );
  
  // Try to create variations or related concepts
  if (keyWords.length > 0) {
    // Use the first significant word and create a related term
    const firstKey = keyWords[0];
    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
    if (firstKey.endsWith('s')) {
      addDistractor(capitalize(firstKey.slice(0, -1))); // Singular form
    } else {
      addDistractor(capitalize(firstKey) + 's'); // Plural form
    }
  }
};

/**
 * Validate a question object for required fields and data integrity
 * 
 * @param {Object} question - Question object to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 * 
 * @example
 * const result = validateQuestion({ question: 'Test?', options: ['A', 'B'], correct: 'A' });
 * // Returns: { isValid: true, errors: [] }
 */
export const validateQuestion = (question) => {
  const errors = [];
  
  if (!question) {
    return { isValid: false, errors: ['Question object is missing'] };
  }
  
  // Check for question text
  if (!question.question && !question.word) {
    errors.push('Question is missing question or word field');
  }
  
  // Check for options array
  if (!Array.isArray(question.options)) {
    errors.push('Options must be an array');
  } else if (question.options.length === 0) {
    errors.push('Options array is empty');
  } else {
    // Check for duplicate options (case-insensitive)
    const optionLower = question.options.map(opt => String(opt).trim().toLowerCase());
    const uniqueOptions = new Set(optionLower);
    if (uniqueOptions.size !== question.options.length) {
      errors.push('Options array contains duplicates');
    }
    
    // Check for empty options
    if (question.options.some(opt => !String(opt).trim())) {
      errors.push('Options array contains empty values');
    }
  }
  
  // Check for correct answer
  const correctAnswer = question.correct || question.answer;
  if (!correctAnswer) {
    errors.push('Correct answer is missing');
  } else if (Array.isArray(question.options)) {
    // Check if correct answer is in options (case-insensitive)
    const correctLower = String(correctAnswer).trim().toLowerCase();
    const optionsLower = question.options.map(opt => String(opt).trim().toLowerCase());
    if (!optionsLower.includes(correctLower)) {
      errors.push('Correct answer is not in options array');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Normalize option lengths for better UI consistency
 * Truncates very long options and ensures similar complexity
 * 
 * @param {Array} options - Array of option strings
 * @param {number} maxLength - Maximum length for options (default: 50)
 * @returns {Array} Normalized options array
 */
export const normalizeOptionLengths = (options, maxLength = 50) => {
  if (!Array.isArray(options)) {
    return options;
  }
  
  return options.map(opt => {
    const str = String(opt).trim();
    if (str.length > maxLength) {
      return str.substring(0, maxLength - 3) + '...';
    }
    return str;
  });
};

/**
 * Automatically determine question difficulty based on content analysis
 * 
 * @param {Object} question - Question object
 * @returns {string} Difficulty level: 'easy', 'medium', or 'hard'
 */
export const determineQuestionDifficulty = (question) => {
  if (!question) return 'medium';
  
  // If difficulty is already set, use it
  if (question.difficulty && ['easy', 'medium', 'hard'].includes(question.difficulty)) {
    return question.difficulty;
  }
  
  const questionText = String(question.question || question.word || '').toLowerCase();
  const correctAnswer = String(question.correct || question.answer || '').toLowerCase();
  const options = Array.isArray(question.options) ? question.options : [];
  
  let difficultyScore = 0;
  
  // Question length - longer questions are often harder
  if (questionText.length > 60) difficultyScore += 1;
  if (questionText.length > 100) difficultyScore += 1;
  
  // Answer complexity - longer answers are often harder
  if (correctAnswer.length > 15) difficultyScore += 1;
  if (correctAnswer.length > 30) difficultyScore += 1;
  
  // Technical/scientific terms indicate harder questions
  const technicalTerms = ['photosynthesis', 'precipitation', 'evaporation', 'condensation', 'hypothesis', 
    'ecosystem', 'habitat', 'carnivore', 'herbivore', 'omnivore', 'constellation', 'atmosphere', 
    'molecule', 'atom', 'gravity', 'friction', 'buoyancy', 'insulator', 'conductor', 'circuit',
    'transcontinental', 'independence', 'declaration', 'constitution', 'revolution'];
  if (technicalTerms.some(term => questionText.includes(term) || correctAnswer.includes(term))) {
    difficultyScore += 2;
  }
  
  // Proper nouns (names, places) indicate medium to hard
  const hasProperNoun = /[A-Z][a-z]+/.test(correctAnswer);
  if (hasProperNoun) difficultyScore += 1;
  
  // Number of options - more options can be harder
  if (options.length >= 4) difficultyScore += 1;
  
  // Math questions with larger numbers are harder
  const correctNum = parseFloat(correctAnswer);
  if (!isNaN(correctNum)) {
    if (correctNum > 20) difficultyScore += 1;
    if (correctNum > 100) difficultyScore += 1;
  }
  
  // Determine difficulty based on score
  if (difficultyScore <= 1) return 'easy';
  if (difficultyScore <= 3) return 'medium';
  return 'hard';
};

/**
 * Add difficulty tags to questions if not already present
 * 
 * @param {Object|Array} questions - Question object or array of questions
 * @returns {Object|Array} Questions with difficulty tags added
 */
export const addDifficultyTags = (questions) => {
  if (Array.isArray(questions)) {
    return questions.map(q => ({
      ...q,
      difficulty: determineQuestionDifficulty(q)
    }));
  }
  
  return {
    ...questions,
    difficulty: determineQuestionDifficulty(questions)
  };
};

/**
 * Expand question options from 2 to 4 for multiple choice questions
 * Does NOT modify true/false questions (keeps them as 2 options)
 * 
 * @param {Object} question - Question object with options array
 * @returns {Object} Question object with expanded options (if applicable)
 * 
 * @example
 * const expanded = expandQuestionOptions({ 
 *   question: 'What is 2 + 3?', 
 *   options: ['4', '5'], 
 *   correct: '5' 
 * });
 * // Returns: { question: 'What is 2 + 3?', options: ['4', '5', '6', '7'], correct: '5' }
 */
export const expandQuestionOptions = (question) => {
  if (!question || !Array.isArray(question.options)) {
    return question;
  }
  
  // Validate question before expanding
  const validation = validateQuestion(question);
  if (!validation.isValid) {
    logger.warn('Question validation failed:', validation.errors, question);
    // Still try to expand, but log the warning
  }
  
  // Don't expand if already has 4+ options
  if (question.options.length >= 4) {
    // Normalize option lengths for consistency
    return {
      ...question,
      options: normalizeOptionLengths(question.options)
    };
  }
  
  // Don't expand true/false questions
  if (isTrueFalseQuestion(question)) {
    return question;
  }
  
  // Only expand 2-option questions
  if (question.options.length === 2) {
    const correctAnswer = question.correct || question.answer || question.options[0];
    const distractors = generateDistractors(question, question.options, correctAnswer);
    
    // Combine existing options with distractors
    const expandedOptions = [...question.options, ...distractors];
    
    // Remove duplicates while preserving order
    const uniqueOptions = [];
    const seen = new Set();
    expandedOptions.forEach(opt => {
      const key = String(opt).trim().toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        uniqueOptions.push(opt);
      }
    });
    
    // Ensure we have 4 options
    while (uniqueOptions.length < 4) {
      uniqueOptions.push(`Option ${uniqueOptions.length + 1}`);
    }
    
    // Normalize option lengths for consistency
    const normalizedOptions = normalizeOptionLengths(uniqueOptions.slice(0, 4));
    
    // Add difficulty tag if not present
    const difficulty = determineQuestionDifficulty(question);
    
    return {
      ...question,
      options: normalizedOptions,
      difficulty: question.difficulty || difficulty
    };
  }
  
  // If 3 options, add one more
  if (question.options.length === 3) {
    const correctAnswer = question.correct || question.answer || question.options[0];
    const distractors = generateDistractors(question, question.options, correctAnswer);
    
    const expandedOptions = [...question.options];
    distractors.forEach(dist => {
      const key = String(dist).trim().toLowerCase();
      if (!expandedOptions.some(opt => String(opt).trim().toLowerCase() === key)) {
        expandedOptions.push(dist);
      }
    });
    
    // Normalize option lengths for consistency
    const normalizedOptions = normalizeOptionLengths(expandedOptions.slice(0, 4));
    
    // Add difficulty tag if not present
    const difficulty = determineQuestionDifficulty(question);
    
    return {
      ...question,
      options: normalizedOptions,
      difficulty: question.difficulty || difficulty
    };
  }
  
  // For questions that already have 4+ options, just add difficulty tag if missing
  if (question.options.length >= 4) {
    const difficulty = determineQuestionDifficulty(question);
    return {
      ...question,
      difficulty: question.difficulty || difficulty
    };
  }
  
  // Add difficulty tag to any remaining questions
  const difficulty = determineQuestionDifficulty(question);
  return {
    ...question,
    difficulty: question.difficulty || difficulty
  };
};

